import type { FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import { type ApiKeyFormValues, apiKeySchema } from '@features/api-keys/components/modals/api-keys.schema';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@shared/components/ui';
import { GenericButton, GenericModal } from '@shared/components/ui/custom';
import { notify } from '@shared/utils';
import type { RTKQueryError } from '@shared/utils/api.types';

import { useCreateNewApiKeyMutation } from '../../api/api-keys.api';

export const CreateApiKeyModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [createApiKey, { isLoading, reset }] = useCreateNewApiKeyMutation();

  const form = useForm<ApiKeyFormValues>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const handleOnClose = () => {
    onClose();
    reset?.();
  };

  const getSubmitButtonText = () => (isLoading ? 'Creating...' : 'Create API Key');

  const onSubmit = async (data: ApiKeyFormValues) => {
    try {
      await createApiKey(data).unwrap();
      toast.success('API Key created successfully!');
      handleOnClose();
    } catch (err) {
      const error = err as RTKQueryError;

      notify({
        type: 'error',
        title: 'Failed to create API Key',
        description: error?.data?.message ?? 'Unexpected error',
      });
    }
  };

  return (
    <GenericModal open title="Create new API Key" onOpenChange={(open) => !open && handleOnClose()}>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate data-testid="api-key-modal-form">
          <div className="flex-1 overflow-y-auto bg-background p-8">
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="max-w-[640px] space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="api-key-name">Name</FormLabel>
                      <FormControl>
                        <Input
                          id="api-key-name"
                          placeholder="Enter API Key name"
                          {...field}
                          data-testid="api-key-modal-input-name"
                          required
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage data-testid="api-key-modal-error-name" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="api-key-description">Description</FormLabel>
                      <FormControl>
                        <Input
                          id="api-key-description"
                          placeholder="Enter API Key description"
                          {...field}
                          required
                          data-testid="api-key-modal-input-description"
                        />
                      </FormControl>
                      <FormMessage data-testid="api-key-modal-error-description" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex items-center gap-3">
                <GenericButton
                  variant="secondary"
                  onClick={handleOnClose}
                  disabled={isLoading}
                  data-testid="api-key-modal-cancel"
                  type="button"
                  label="Cancel"
                />
                <GenericButton
                  type="submit"
                  isLoading={isLoading}
                  data-testid="api-key-modal-submit"
                  label={getSubmitButtonText()}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </GenericModal>
  );
};
